<template>
  <ValidationObserver ref="observer" v-slot="{ handleSubmit }">
    <b-modal
      v-model="isOpen"
      has-modal-card
      trap-focus
      aria-role="dialog"
      aria-label="ActualitesModal"
      :aria-modal="true"
      scroll="keep"
      class="ActualitesModal"
    >
      <div class="modal-card">
        <header class="modal-card-head">
          <H3TitleView v-if="actualite.id" class="modal-card-title"
            >{{ actualite.id ? $t("actualitesPage.update") : $t("actualitesPage.create") }}
          </H3TitleView>
          <div class="ActualitesModalView-infos">
            {{
              `${$t("actualitesPage.date-creation")} ${formatDate(internalActualite.dateCreation)}${
                actualite.auteur
                  ? " par " + actualite.auteur.prenom + " " + actualite.auteur.nom
                  : ""
              }`
            }}
          </div>
        </header>
        <section class="modal-card-body">
          <SelectWithValidation
            v-model="internalActualite.type"
            :label="$t('actualitesPage.actualite-type-modal.type-list')"
            :data="actualiteTypes"
            :select-labels="actualiteTypeLabels"
            vid="actualiteType"
            rules="required"
            :disabled="readOnly"
          />

          <DatePickerWithValidation
            v-model="internalActualite.dateDebutPublication"
            vid="actu-debut"
            rules="required"
            :label="$t('actualitesPage.date-debut')"
            :placeholder="$t('ParametersPage.selectionner-date')"
            :disabled="readOnly"
          />

          <DatePickerWithValidation
            v-model="internalActualite.dateFinPublication"
            vid="actu-fin"
            :rules="`${
              internalActualite.dateDebutPublication
                ? `dateIsAfter:${internalActualite.dateDebutPublication.toString()}`
                : ''
            }|required`"
            :label="$t('actualitesPage.date-fin')"
            :placeholder="$t('ParametersPage.selectionner-date')"
            :disabled="readOnly"
          />

          <InputWithValidation
            v-model="internalActualite.titre"
            type="textarea"
            :label="$t('actualitesPage.actu-title')"
            maxlength="80"
            vid="actu-title"
            rules="required"
            :disabled="readOnly"
          />

          <InputWithValidation
            v-model="internalActualite.contenu"
            type="textarea"
            :label="$t('actualitesPage.content')"
            maxlength="1400"
            vid="actu-content"
            rules="required"
            :disabled="readOnly"
          />
        </section>

        <footer class="modal-card-foot">
          <div class="buttons">
            <div class="hoverable-discrete-link mr-3" @click="emitCloseModale">
              {{ $t("close-generic") }}
            </div>
            <b-button
              v-if="actualite.id && !readOnly"
              :label="$t('profilePage.save').toString()"
              type="is-primary"
              rounded
              @click="handleSubmit(saveModifications)"
            />
            <b-button
              v-else-if="!actualite.id && !readOnly"
              :label="$t('actualitesPage.create-actualite').toString()"
              type="is-primary"
              rounded
              @click="handleSubmit(createNewActualite)"
            />
          </div>
        </footer>
      </div>
    </b-modal>
  </ValidationObserver>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import { ActualiteDTO, ActualiteTypeDTO, UtilisateurDTO } from "@/model/bean/GeneratedDTOs"
import H3TitleView from "../commons/H3TitleView.vue"
import DatePickerWithValidation from "../inputs/DatePickerWithValidation.vue"
import InputWithValidation from "../inputs/InputWithValidation.vue"
import { LoginService } from "@/services/rest/login/LoginService"
import ValidationObserver from "@/services/validation/ValidationObserver.vue"
import SelectWithValidation from "@/views/inputs/SelectWithValidation.vue"
import { BButton, BModal } from "buefy"
import DateUtils from "@/services/DateUtils"

export default defineComponent({
  name: "ActualitesModalView",
  components: {
    SelectWithValidation,
    H3TitleView,
    InputWithValidation,
    DatePickerWithValidation,
    ValidationObserver,
    BButton,
    BModal,
  },
  props: {
    actualite: {
      type: Object as PropType<ActualiteDTO>,
      required: false,
      default: () => new ActualiteDTO(),
    },
    actualiteTypes: {
      type: Array as PropType<ActualiteTypeDTO[]>,
      required: false,
      default: () => [],
    },
    open: {
      type: Boolean,
      required: false,
      default: false,
    },
    readOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: ["close", "close-refresh"],
  data() {
    return {
      loginService: LoginService.INSTANCE,

      loggedUser: new UtilisateurDTO() as UtilisateurDTO | undefined,
      internalActualite: new ActualiteDTO(),
      actualiteTypeLabels: [] as string[],
    }
  },
  computed: {
    isOpen: {
      get(): boolean {
        return this.open
      },
      set(value: boolean): void {
        if (!value) {
          this.$emit("close")
        }
      },
    },
  },
  watch: {
    actualite(): void {
      this.assignActualiteToInternal()
    },
    actualiteTypes(): void {
      if (this.actualiteTypes) {
        this.actualiteTypeLabels = this.actualiteTypes.map((value) => {
          return value.label
        })
      }
    },
  },
  created(): void {
    this.loggedUser = this.loginService.getLoggedUser()
    this.assignActualiteToInternal()
  },
  methods: {
    assignActualiteToInternal(): void {
      this.internalActualite = this.actualite

      if (!this.actualite.id) {
        this.internalActualite.dateCreation = new Date()
        if (this.loggedUser) {
          this.internalActualite.auteur = this.loggedUser
        }
      }
    },

    emitCloseModale(): void {
      this.$emit("close")
    },

    saveModifications(): void {
      this.$emit("close-refresh", this.internalActualite)
    },

    createNewActualite(): void {
      this.$emit("close-refresh", this.internalActualite)
    },

    formatDate(date: Date): string {
      return DateUtils.formatDate(date)
    },
  },
})
</script>

<style lang="scss">
.ActualitesModal {
  &.modal .modal-card {
    height: 75%;
    overflow-y: auto !important;
  }

  span.select {
    width: 100%;
  }

  .animation-content {
    height: 100%;
  }
}

.ActualitesModalView-infos {
  font-style: italic;
}
</style>
